/**
 * useSavedViews Composable
 *
 * Per-entity view state management. Handles view CRUD, selection,
 * schema caching, unsaved change detection, RBAC enforcement,
 * and most-recently-used (MRU) view caching.
 *
 * Usage:
 *   const { views, activeView, ... } = useSavedViews('property')
 */

import { ref, computed, type Ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { savedViewService } from '../api/service'
import {
    getRecentViewId,
    setRecentViewId,
    clearRecentViewId as clearRecentCache,
    removeRecentViewCache,
    NONE_SENTINEL,
} from './recentViewCache'
import type {
    SavedView,
    SavedViewEntityType,
    SavedViewVisibility,
    FilterGroup,
    SortConfig,
    EntitySchema,
    SchemaField,
} from '../api/types'

// ─── Schema cache (shared across instances) ─────────────────────────

const schemaCache = new Map<SavedViewEntityType, EntitySchema>()
let availableEntityTypes: SavedViewEntityType[] | null = null

/**
 * Client-side fallback schemas for entity types not yet registered in the backend.
 * Once the backend registers its own schema, the API response takes priority.
 */
const fallbackSchemas: Partial<Record<SavedViewEntityType, EntitySchema>> = {
    fleet_machine: {
        entityType: 'fleet_machine',
        label: 'Machines',
        fields: [
            { name: 'hostname', type: 'string', label: 'Hostname', operators: ['$eq', '$contains'] },
            { name: 'username', type: 'string', label: 'User', operators: ['$eq', '$contains'] },
            { name: 'userPrincipalName', type: 'string', label: 'UPN / Email', operators: ['$eq', '$contains'] },
            { name: 'status', type: 'enum', label: 'Status', operators: ['$eq', '$ne'], values: ['approved', 'pending', 'rejected', 'revoked'] },
            { name: 'osVersion', type: 'string', label: 'OS Version', operators: ['$eq', '$contains'] },
            { name: 'agentVersion', type: 'string', label: 'Agent Version', operators: ['$eq', '$contains'] },
        ],
    },
    fleet_app_classification: {
        entityType: 'fleet_app_classification',
        label: 'App Classifications',
        fields: [
            { name: 'processName', type: 'string', label: 'Application', operators: ['$eq', '$contains'] },
            { name: 'classification', type: 'enum', label: 'Classification', operators: ['$eq', '$ne'], values: ['productive', 'neutral', 'unproductive', 'idle'] },
        ],
    },
    fleet_domain_classification: {
        entityType: 'fleet_domain_classification',
        label: 'Domain Classifications',
        fields: [
            { name: 'domain', type: 'string', label: 'Domain', operators: ['$eq', '$contains'] },
            { name: 'classification', type: 'enum', label: 'Classification', operators: ['$eq', '$ne'], values: ['productive', 'neutral', 'unproductive', 'idle'] },
        ],
    },
    fleet_daily_summary: {
        entityType: 'fleet_daily_summary',
        label: 'Daily Summaries',
        fields: [
            { name: 'username', type: 'string', label: 'Employee', operators: ['$eq', '$contains'] },
            { name: 'date', type: 'date', label: 'Date', operators: ['$gte', '$lte', '$eq'] },
        ],
    },
    lead: {
        entityType: 'lead',
        label: 'Leads',
        fields: [
            { name: 'title', type: 'string', label: 'Lead Title', operators: ['$eq', '$contains'] },
            { name: 'status', type: 'enum', label: 'Status', operators: ['$eq', '$ne', '$in', '$nin'], values: ['new', 'contacted', 'engaged', 'qualified', 'unqualified', 'converted'] },
            { name: 'priority', type: 'enum', label: 'Priority', operators: ['$eq', '$ne', '$in', '$nin'], values: ['low', 'medium', 'high', 'critical'] },
            { name: 'source', type: 'enum', label: 'Source', operators: ['$eq', '$ne', '$in', '$nin'], values: ['storm_tracker', 'web_form', 'campaign', 'manual', 'referral', 'enrichment'] },
            { name: 'score', type: 'number', label: 'Score', operators: ['$gte', '$lte', '$eq'] },
            { name: 'companyName', type: 'string', label: 'Company', operators: ['$eq', '$contains'] },
            { name: 'primaryContactName', type: 'string', label: 'Contact Name', operators: ['$eq', '$contains'] },
            { name: 'primaryContactEmail', type: 'string', label: 'Contact Email', operators: ['$eq', '$contains'] },
            { name: 'createdAt', type: 'date', label: 'Created At', operators: ['$gte', '$lte', '$eq'] },
        ],
        systemViews: [
            {
                name: 'All Open Leads',
                entityType: 'lead',
                position: 0,
                filters: {
                    operator: 'AND',
                    conditions: [
                        { field: 'status', op: '$nin', value: ['converted', 'unqualified'] },
                    ],
                },
                sort: { field: 'createdAt', direction: 'desc' },
            },
            {
                name: 'Severe Weather Leads',
                entityType: 'lead',
                position: 1,
                filters: {
                    operator: 'AND',
                    conditions: [
                        { field: 'source', op: '$eq', value: 'storm_tracker' },
                    ],
                },
                sort: { field: 'createdAt', direction: 'desc' },
            },
            {
                name: 'Inbound Web Inquiries',
                entityType: 'lead',
                position: 2,
                filters: {
                    operator: 'AND',
                    conditions: [
                        { field: 'source', op: '$eq', value: 'web_form' },
                    ],
                },
                sort: { field: 'createdAt', direction: 'desc' },
            },
            {
                name: 'High Priority Targets',
                entityType: 'lead',
                position: 3,
                filters: {
                    operator: 'AND',
                    conditions: [
                        { field: 'status', op: '$ne', value: 'converted' },
                        {
                            operator: 'OR',
                            conditions: [
                                { field: 'priority', op: '$in', value: ['high', 'critical'] },
                                { field: 'score', op: '$gte', value: 70 },
                            ],
                        },
                    ],
                },
                sort: { field: 'score', direction: 'desc' },
            },
        ],
    },
    optimizer_material_preset: {
        entityType: 'optimizer_material_preset',
        label: 'Material Presets',
        fields: [
            { name: 'name', type: 'string', label: 'Material Name', operators: ['$eq', '$contains', '$startsWith'] },
            { name: 'materialType', type: 'string', label: 'Material Type', operators: ['$eq', '$ne', '$in', '$nin', '$contains', '$exists'] },
            { name: 'vendor', type: 'string', label: 'Vendor', operators: ['$eq', '$ne', '$in', '$nin', '$contains', '$exists'] },
            { name: 'finish', type: 'string', label: 'Finish', operators: ['$eq', '$ne', '$in', '$nin', '$contains', '$exists'] },
            { name: 'thickness', type: 'string', label: 'Thickness', operators: ['$eq', '$ne', '$in', '$nin', '$contains', '$exists'] },
            { name: 'isDirectional', type: 'boolean', label: 'Directional Grain', operators: ['$eq'] },
            { name: 'kerfThickness', type: 'number', label: 'Kerf Thickness (in)', operators: ['$eq', '$gt', '$gte', '$lt', '$lte'] },
            { name: 'returnLeg', type: 'number', label: 'Return Leg (in)', operators: ['$eq', '$gt', '$gte', '$lt', '$lte'] },
            { name: 'fillerStripWidth', type: 'number', label: 'Filler Strip Width (in)', operators: ['$eq', '$gt', '$gte', '$lt', '$lte'] },
            { name: 'perimeterMargin', type: 'number', label: 'Perimeter Margin (in)', operators: ['$eq', '$gt', '$gte', '$lt', '$lte'] },
            { name: 'customLengthMinSqft', type: 'number', label: 'Custom Min Sqft', operators: ['$eq', '$gt', '$gte', '$lt', '$lte'] },
            { name: 'minSheetsPerLength', type: 'number', label: 'Min Sheets per Length', operators: ['$eq', '$gt', '$gte', '$lt', '$lte'] },
            { name: 'createdAt', type: 'date', label: 'Created Date', operators: ['$gt', '$gte', '$lt', '$lte'] },
            { name: 'updatedAt', type: 'date', label: 'Updated Date', operators: ['$gt', '$gte', '$lt', '$lte'] },
        ],
    },
}

/**
 * Enrich a backend-provided schema so that enum fields always support
 * $in ("is any of") and $nin ("is none of") operators.
 * The frontend filter UI already handles multi-select for these operators,
 * but some backend schemas may not advertise them yet.
 */
function enrichSchemaOperators(schema: EntitySchema): void {
    for (const field of schema.fields) {
        if (field.type === 'enum' || field.type === 'relation') {
            if (!field.operators.includes('$in')) field.operators.push('$in')
            if (!field.operators.includes('$nin')) field.operators.push('$nin')
        }
    }
}

/** @internal Test-only: resets module-level caches */
export function _resetCacheForTesting() {
    schemaCache.clear()
    availableEntityTypes = null
}

/** System default fallback views for core entity types */
const defaultSystemViews: Partial<Record<SavedViewEntityType, SavedView>> = {
    optimizer_job: {
        id: 'sys-optimizer-my-jobs',
        name: 'My Optimization Jobs',
        entityType: 'optimizer_job',
        type: 'dynamic',
        visibility: 'system',
        isDefault: true,
        position: 0,
        filters: {
            operator: 'AND',
            conditions: [{ field: 'createdByUser', op: '$in', value: ['$CURRENT_USER'] }],
        },
        createdBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
    it_request: {
        id: 'sys-it-submitted-by-me',
        name: 'Submitted by Me',
        entityType: 'it_request',
        type: 'dynamic',
        visibility: 'system',
        isDefault: true,
        position: 0,
        filters: {
            operator: 'AND',
            conditions: [{ field: 'submitter', op: '$in', value: ['$CURRENT_USER'] }],
        },
        createdBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
    fab_job: {
        id: 'sys-fab-my-jobs',
        name: 'My Fab Jobs',
        entityType: 'fab_job',
        type: 'dynamic',
        visibility: 'system',
        isDefault: true,
        position: 0,
        filters: {
            operator: 'AND',
            conditions: [{ field: 'projectManagerUser', op: '$in', value: ['$CURRENT_USER'] }],
        },
        createdBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
    lead: {
        id: 'sys-lead-all-open',
        name: 'All Open Leads',
        entityType: 'lead',
        type: 'dynamic',
        visibility: 'system',
        isDefault: true,
        position: 0,
        filters: {
            operator: 'AND',
            conditions: [
                { field: 'status', op: '$nin', value: ['converted', 'unqualified'] },
            ],
        },
        createdBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
}

/**
 * Composable for managing saved views for a specific entity type.
 *
 * @param entityType - The entity type this instance manages views for.
 */
export function useSavedViews(entityType: SavedViewEntityType) {
    const authStore = useAuthStore()
    const uiStore = useUiStore()

    // ─── State ──────────────────────────────────────────────────

    const views: Ref<SavedView[]> = ref([])
    const activeView: Ref<SavedView | null> = ref(null)
    const schema: Ref<EntitySchema | null> = ref(null)
    const isLoading = ref(false)
    const error: Ref<string | null> = ref(null)

    /**
     * The filters/sort that were active when the view was selected.
     * Used to detect unsaved changes.
     */
    const savedFilters: Ref<FilterGroup | null> = ref(null)
    const savedSort: Ref<SortConfig | null> = ref(null)

    // ─── Computed ───────────────────────────────────────────────

    /** Whether the current user can create/edit views */
    const canEdit = computed(() => true)

    /** Views grouped by visibility */
    const myViews = computed(() =>
        views.value.filter(v => v.visibility === 'private' && (v.createdBy?.id === authStore.user?.id || !v.createdBy))
    )
    const sharedViews = computed(() =>
        views.value.filter(v => v.visibility === 'shared')
    )
    const systemViews = computed(() =>
        views.value.filter(v => v.visibility === 'system')
    )

    /** Schema fields for the current entity type */
    const fields = computed((): SchemaField[] => schema.value?.fields ?? [])

    /** Whether there is an active view selected */
    const hasActiveView = computed(() => activeView.value !== null)

    // ─── Schema ─────────────────────────────────────────────────

    /**
     * Discover available entity types from the API (cached).
     */
    async function discoverEntityTypes(): Promise<SavedViewEntityType[]> {
        if (availableEntityTypes) return availableEntityTypes

        try {
            const schemas = await savedViewService.getSchemaList()
            availableEntityTypes = schemas.map(s => s.entityType)
            return availableEntityTypes
        } catch (err) {
            console.error('Failed to discover entity schemas:', err)
            return []
        }
    }

    /**
     * Load the entity schema (cached).
     * First discovers available types, then only fetches if this type is supported.
     * Falls back to client-side schema when the backend doesn't have one registered.
     */
    async function loadSchema(): Promise<EntitySchema | null> {
        // Check cache first
        const cached = schemaCache.get(entityType)
        if (cached) {
            schema.value = cached
            return cached
        }

        // Discover available types before requesting
        const available = await discoverEntityTypes()
        if (!available.includes(entityType)) {
            // Backend doesn't have this type yet — use frontend fallback
            const fallback = fallbackSchemas[entityType]
            if (fallback) {
                schema.value = fallback
                return fallback
            }
            return null
        }

        try {
            const result = await savedViewService.getSchema(entityType)
            enrichSchemaOperators(result)
            schemaCache.set(entityType, result)
            schema.value = result
            return result
        } catch (err) {
            console.error(`Failed to load schema for ${entityType}:`, err)
            error.value = 'Failed to load filter fields'
            return null
        }
    }

    // ─── View CRUD ──────────────────────────────────────────────

    /**
     * Load all views for this entity type.
     */
    async function loadViews(): Promise<void> {
        isLoading.value = true
        error.value = null

        try {
            const [listRes, defaultRes] = await Promise.allSettled([
                savedViewService.list({ entityType }),
                savedViewService.getDefault(entityType),
            ])

            let loadedViews: SavedView[] = []
            if (listRes.status === 'fulfilled') {
                loadedViews = [...listRes.value]
            } else {
                console.error(`Failed to load views for ${entityType}:`, listRes.reason)
                error.value = 'Failed to load saved views'
                uiStore.error('Failed to load saved views')
            }

            // Include default view returned by the backend if present
            if (defaultRes.status === 'fulfilled' && defaultRes.value) {
                const def = defaultRes.value
                const existingIndex = loadedViews.findIndex(
                    v => v.id === def.id || (v.entityType === def.entityType && v.name.toLowerCase() === def.name.toLowerCase())
                )
                if (existingIndex !== -1) {
                    loadedViews[existingIndex] = {
                        ...loadedViews[existingIndex],
                        ...def,
                        id: loadedViews[existingIndex].id || def.id,
                        isDefault: true,
                    }
                } else {
                    loadedViews.push({ ...def, isDefault: true })
                }
            }

            // Ingest schema systemViews if defined on the entity schema
            if (schema.value?.systemViews && schema.value.systemViews.length > 0) {
                for (const sysView of schema.value.systemViews) {
                    const hasExisting = loadedViews.some(
                        v => v.visibility === 'system' && v.name.toLowerCase() === sysView.name.toLowerCase()
                    )
                    if (!hasExisting) {
                        loadedViews.push({
                            id: `sys-${entityType}-${sysView.name.toLowerCase().replace(/\s+/g, '-')}`,
                            name: sysView.name,
                            entityType,
                            type: 'dynamic',
                            visibility: 'system',
                            position: sysView.position ?? 0,
                            filters: sysView.filters,
                            sort: Array.isArray(sysView.sort) ? sysView.sort[0] : sysView.sort,
                            createdBy: null,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                        })
                    }
                }
            }

            // Fallback system view if no system views exist in the list
            const fallbackDef = defaultSystemViews[entityType]
            if (fallbackDef) {
                const hasSystemView = loadedViews.some(
                    v => v.visibility === 'system' || v.name.toLowerCase() === fallbackDef.name?.toLowerCase()
                )
                if (!hasSystemView) {
                    loadedViews.push(fallbackDef)
                }
            }

            // Final deduplication: system views by name, user views by id/name
            const uniqueViewsMap = new Map<string, SavedView>()
            for (const view of loadedViews) {
                const key = view.visibility === 'system'
                    ? `${view.entityType}:system:${view.name?.toLowerCase().trim()}`
                    : `${view.entityType}:${view.id || view.name?.toLowerCase().trim()}`

                const existing = uniqueViewsMap.get(key)
                if (!existing) {
                    uniqueViewsMap.set(key, view)
                } else {
                    uniqueViewsMap.set(key, {
                        ...existing,
                        ...view,
                        id: existing.id || view.id,
                        isDefault: existing.isDefault || view.isDefault,
                    })
                }
            }

            views.value = Array.from(uniqueViewsMap.values()).sort((a, b) => a.position - b.position)
        } catch (err) {
            console.error(`Failed to load views for ${entityType}:`, err)
            error.value = 'Failed to load saved views'
            uiStore.error('Failed to load saved views')
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Select a view and return its filters/sort for the parent to apply.
     * Guards against rapid switching (last call wins).
     */
    let selectGeneration = 0

    async function selectView(viewId: string): Promise<{ filters?: FilterGroup; sort?: SortConfig } | null> {
        const generation = ++selectGeneration
        error.value = null

        const view = views.value.find(v => v.id === viewId)
        if (!view) {
            error.value = 'View not found'
            return null
        }

        // Guard: if another selectView was called, abort
        if (generation !== selectGeneration) return null

        activeView.value = view
        savedFilters.value = view.filters ? JSON.parse(JSON.stringify(view.filters)) : null
        savedSort.value = view.sort ? JSON.parse(JSON.stringify(view.sort)) : null

        // Cache this view as the most recently used for this entity type
        setRecentViewId(entityType, viewId, authStore.user?.id)

        return {
            filters: view.filters,
            sort: view.sort,
        }
    }

    /**
     * Clear the active view (return to unfiltered state).
     */
    function clearView(): void {
        activeView.value = null
        savedFilters.value = null
        savedSort.value = null

        // Record explicit user reset so revisiting stays unfiltered
        clearRecentCache(entityType, authStore.user?.id)
    }

    /**
     * Save current filters/sort as a new named view.
     */
    async function saveCurrentFilters(
        name: string,
        filters: FilterGroup,
        sort?: SortConfig,
        visibility: SavedViewVisibility = 'private',
    ): Promise<SavedView | null> {
        if (!canEdit.value) {
            error.value = 'You do not have permission to save views'
            return null
        }

        error.value = null

        try {
            const newView = await savedViewService.create({
                name,
                entityType,
                filters,
                sort,
                visibility,
            })

            views.value.push(newView)
            activeView.value = newView
            savedFilters.value = JSON.parse(JSON.stringify(filters))
            savedSort.value = sort ? JSON.parse(JSON.stringify(sort)) : null

            // Cache the newly created view as most recently used
            setRecentViewId(entityType, newView.id, authStore.user?.id)

            uiStore.success(`View "${name}" saved`)
            return newView
        } catch (err) {
            console.error('Failed to save view:', err)
            error.value = 'Failed to save view'
            uiStore.error('Failed to save view')
            return null
        }
    }

    /**
     * Update an existing view.
     */
    async function updateView(
        viewId: string,
        updates: { name?: string; filters?: FilterGroup; sort?: SortConfig; visibility?: SavedViewVisibility; position?: number },
    ): Promise<SavedView | null> {
        if (!canEdit.value) {
            error.value = 'You do not have permission to edit views'
            return null
        }

        error.value = null

        try {
            const updated = await savedViewService.update(viewId, updates)

            // Replace in the list
            const index = views.value.findIndex(v => v.id === viewId)
            if (index !== -1) {
                views.value[index] = updated
            }

            // Update active view if it was the one being edited
            if (activeView.value?.id === viewId) {
                activeView.value = updated
                if (updates.filters) {
                    savedFilters.value = JSON.parse(JSON.stringify(updates.filters))
                }
                if (updates.sort) {
                    savedSort.value = JSON.parse(JSON.stringify(updates.sort))
                }
            }

            uiStore.success('View updated')
            return updated
        } catch (err) {
            console.error('Failed to update view:', err)
            error.value = 'Failed to update view'
            uiStore.error('Failed to update view')
            return null
        }
    }

    /**
     * Delete a view.
     */
    async function deleteView(viewId: string): Promise<boolean> {
        if (!canEdit.value) {
            error.value = 'You do not have permission to delete views'
            return false
        }

        error.value = null

        try {
            await savedViewService.remove(viewId)
            views.value = views.value.filter(v => v.id !== viewId)

            if (activeView.value?.id === viewId) {
                // Clearing the active view writes __none__ to the MRU cache,
                // which prevents auto-restore on next visit.
                clearView()
            } else {
                // The deleted view was not active — clean up the MRU cache
                // only if this specific view was the cached one.
                const cachedId = getRecentViewId(entityType, authStore.user?.id)
                if (cachedId === viewId) {
                    removeRecentViewCache(entityType, authStore.user?.id)
                }
            }

            uiStore.success('View deleted')
            return true
        } catch (err) {
            console.error('Failed to delete view:', err)
            error.value = 'Failed to delete view'
            uiStore.error('Failed to delete view')
            return false
        }
    }

    /**
     * Duplicate a view.
     */
    async function duplicateView(viewId: string): Promise<SavedView | null> {
        if (!canEdit.value) {
            error.value = 'You do not have permission to duplicate views'
            return null
        }

        error.value = null

        try {
            const copy = await savedViewService.duplicate(viewId)
            views.value.push(copy)
            uiStore.success(`View "${copy.name}" created`)
            return copy
        } catch (err) {
            console.error('Failed to duplicate view:', err)
            error.value = 'Failed to duplicate view'
            uiStore.error('Failed to duplicate view')
            return null
        }
    }

    // ─── Lifecycle ──────────────────────────────────────────────

    /**
     * Freeze a dynamic view into a static snapshot.
     */
    async function freezeView(viewId: string): Promise<SavedView | null> {
        error.value = null
        try {
            const frozen = await savedViewService.freeze(viewId)
            const index = views.value.findIndex(v => v.id === viewId)
            if (index !== -1) views.value[index] = frozen
            if (activeView.value?.id === viewId) activeView.value = frozen
            uiStore.success('View frozen — results are now a static snapshot')
            return frozen
        } catch (err) {
            console.error('Failed to freeze view:', err)
            error.value = 'Failed to freeze view'
            uiStore.error('Failed to freeze view')
            return null
        }
    }

    /**
     * Unfreeze a static view back to dynamic.
     */
    async function unfreezeView(viewId: string): Promise<SavedView | null> {
        error.value = null
        try {
            const unfrozen = await savedViewService.unfreeze(viewId)
            const index = views.value.findIndex(v => v.id === viewId)
            if (index !== -1) views.value[index] = unfrozen
            if (activeView.value?.id === viewId) activeView.value = unfrozen
            uiStore.success('View unfrozen — results are now live')
            return unfrozen
        } catch (err) {
            console.error('Failed to unfreeze view:', err)
            error.value = 'Failed to unfreeze view'
            uiStore.error('Failed to unfreeze view')
            return null
        }
    }

    // ─── Unsaved Changes Detection ──────────────────────────────

    /**
     * Check if the current filters differ from the saved view's filters.
     * Call this with the current filter state from the parent component.
     */
    function checkUnsavedChanges(currentFilters?: FilterGroup, currentSort?: SortConfig): boolean {
        if (!activeView.value) return false
        if (!savedFilters.value && !currentFilters) return false

        const filtersChanged = JSON.stringify(currentFilters ?? null) !== JSON.stringify(savedFilters.value ?? null)
        const sortChanged = JSON.stringify(currentSort ?? null) !== JSON.stringify(savedSort.value ?? null)

        return filtersChanged || sortChanged
    }

    /** View marked as default for this entity type (if loaded) */
    const defaultView = computed(() => views.value.find(v => v.isDefault) ?? null)

    // ─── Default View Preferences ───────────────────────────────────

    /**
     * Mark a saved view as the user's default for this entity type.
     */
    async function setDefaultView(viewId: string): Promise<SavedView | null> {
        error.value = null
        try {
            const updated = await savedViewService.setDefault(viewId)
            views.value = views.value.map(v => ({
                ...v,
                isDefault: v.id === viewId,
            }))
            if (activeView.value?.id === viewId) {
                activeView.value.isDefault = true
            }
            uiStore.success(`View "${updated.name}" set as default`)
            return updated
        } catch (err) {
            console.error('Failed to set default view:', err)
            error.value = 'Failed to set default view'
            uiStore.error('Failed to set default view')
            return null
        }
    }

    /**
     * Reset default view preference for this entity type (reverts to system view).
     */
    async function resetDefaultView(): Promise<boolean> {
        error.value = null
        try {
            await savedViewService.resetDefault(entityType)
            views.value = views.value.map(v => ({
                ...v,
                isDefault: false,
            }))
            if (activeView.value) {
                activeView.value.isDefault = false
            }
            uiStore.success('Default view preference reset')
            return true
        } catch (err) {
            console.error('Failed to reset default view:', err)
            error.value = 'Failed to reset default view'
            uiStore.error('Failed to reset default view')
            return false
        }
    }

    // ─── MRU View & Default View Restoration ───────────────────────

    /**
     * Attempt to restore the landing view when navigating to a list page.
     *
     * Precedence:
     * 1. If MRU cache has NONE_SENTINEL (user explicitly clicked "All"), return null (stay unfiltered).
     * 2. If MRU cache has a valid view ID, select and return it.
     * 3. If no MRU cache entry exists (or cache entry was stale), fall back to
     *    the user/system default view via GET /saved-views/default/:entityType.
     *
     * @returns The filter/sort result from selectView, or null if no view should be restored.
     */
    async function restoreRecentView(): Promise<{ filters?: FilterGroup; sort?: SortConfig } | null> {
        const cachedId = getRecentViewId(entityType, authStore.user?.id)

        // User explicitly cleared → stay unfiltered
        if (cachedId === NONE_SENTINEL) return null

        // Valid cached view → restore it
        if (cachedId) {
            const viewExists = views.value.some(v => v.id === cachedId)
            if (viewExists) {
                return selectView(cachedId)
            }
            // Stale cache entry — purge it
            removeRecentViewCache(entityType, authStore.user?.id)
        }

        // Fall back to default view (user preference or system default)
        try {
            const def = await savedViewService.getDefault(entityType)
            if (def) {
                const inList = views.value.some(v => v.id === def.id)
                if (!inList) {
                    views.value.push(def)
                }
                return selectView(def.id)
            }
        } catch {
            if (defaultView.value) {
                return selectView(defaultView.value.id)
            }
        }

        return null
    }

    // ─── View Count ─────────────────────────────────────────────

    /**
     * Load result counts for all views (for badges in the switcher).
     * Uses batched requests to avoid overwhelming the API.
     */
    async function loadViewCounts(): Promise<Map<string, number>> {
        const counts = new Map<string, number>()

        // Process in batches of 5
        const batchSize = 5
        for (let i = 0; i < views.value.length; i += batchSize) {
            const batch = views.value.slice(i, i + batchSize)
            const results = await Promise.allSettled(
                batch.map(v => savedViewService.getCount(v.id))
            )

            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    counts.set(batch[index].id, result.value.count)
                }
            })
        }

        return counts
    }

    return {
        // State
        views,
        activeView,
        schema,
        fields,
        isLoading,
        error,

        // Computed
        canEdit,
        myViews,
        sharedViews,
        systemViews,
        hasActiveView,
        defaultView,

        // Schema
        loadSchema,

        // CRUD
        loadViews,
        selectView,
        clearView,
        saveCurrentFilters,
        updateView,
        deleteView,
        duplicateView,
        setDefaultView,
        resetDefaultView,

        // Lifecycle
        freezeView,
        unfreezeView,

        // Unsaved changes
        checkUnsavedChanges,

        // MRU restoration
        restoreRecentView,

        // Counts
        loadViewCounts,
    }
}
